import { useState } from 'react'
import { ChevronDown, Link2, NotebookText } from 'lucide-react'
import type { SizingField } from '@ispot/shared'
import {
  CONFIDENCE_META,
  formatValue,
  parseShorthand,
  stringifyForInput,
  type Confidence,
  type DerivationInputs,
  type Reconcile,
} from './sizingView'
import { RationaleHint } from '../shared/RationaleHint'
import { DerivationPanel } from './DerivationPanel'

interface Props {
  label: string
  unit: 'currency' | 'percent'
  field: SizingField
  confidence: Confidence
  derivation: DerivationInputs
  /** Shown on the TAM row when both SAM sides resolve. */
  reconcile?: Reconcile | null
  /** Called on value commit; the section handles persistence. */
  onSubmit: (value: number | null) => Promise<void>
  /** Start in expanded state (TAM when sizing first opens). */
  defaultOpen?: boolean
}

export function SizingRow({
  label,
  unit,
  field,
  confidence,
  derivation,
  reconcile,
  onSubmit,
  defaultOpen = false,
}: Props) {
  const [open, setOpen] = useState(defaultOpen)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(() => stringifyForInput(field.value, unit))

  const toggle = () => setOpen((o) => !o)
  const commit = async () => {
    setEditing(false)
    const next = parseShorthand(draft, unit)
    if (next === field.value) return
    await onSubmit(next)
  }
  const cancel = () => {
    setDraft(stringifyForInput(field.value, unit))
    setEditing(false)
  }

  const meta = CONFIDENCE_META[confidence]
  const sourceCount = derivation.sources.length
  const assumptionCount = derivation.assumptions.length

  return (
    <div
      className="group rounded-[14px] transition-colors"
      style={{
        backgroundColor: open ? 'var(--color-warm-50)' : 'transparent',
      }}
    >
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggle()
          }
        }}
        className="grid w-full cursor-pointer items-center gap-4 rounded-[14px] px-4 py-3.5 text-left transition-colors hover:bg-[var(--color-warm-100)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-market)]"
        style={{ gridTemplateColumns: '72px minmax(140px, 156px) 1fr auto' }}
      >
        <span
          className="text-[11px] font-semibold uppercase tracking-[0.08em]"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {label}
        </span>

        <div onClick={(e) => e.stopPropagation()} className="flex items-center">
          {editing ? (
            <input
              autoFocus
              type="text"
              inputMode="decimal"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void commit()
                if (e.key === 'Escape') cancel()
              }}
              className="w-[130px] rounded border px-2 py-1 text-lg font-semibold tabular-nums"
              style={{
                borderColor: 'var(--color-border-strong)',
                fontFamily: 'var(--font-display, inherit)',
              }}
              placeholder={unit === 'currency' ? 'e.g. 12.4B' : 'e.g. 12'}
            />
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setDraft(stringifyForInput(field.value, unit))
                setEditing(true)
              }}
              className="font-display text-[24px] font-semibold tabular-nums tracking-[-0.02em] transition-colors hover:text-[var(--color-market)]"
              style={{
                color:
                  field.value == null ? 'var(--color-text-muted)' : 'var(--color-text)',
              }}
            >
              {field.value == null ? 'Click to add' : formatValue(field.value, unit)}
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5"
            style={{ backgroundColor: meta.bg }}
          >
            <span
              className="inline-block h-[6px] w-[6px] rounded-full"
              style={{ backgroundColor: meta.dot }}
            />
            <span className="text-[11px] font-medium" style={{ color: meta.fg }}>
              {meta.label}
            </span>
          </span>

          <CountChip
            icon={<Link2 className="h-3 w-3" />}
            count={sourceCount}
            label={sourceCount === 1 ? 'source' : 'sources'}
            active={sourceCount > 0}
          />
          <CountChip
            icon={<NotebookText className="h-3 w-3" />}
            count={assumptionCount}
            label={assumptionCount === 1 ? 'assumption' : 'assumptions'}
            active={assumptionCount > 0}
          />
          {sourceCount === 0 ? (
            <span className="text-[11px] italic" style={{ color: 'var(--color-text-muted)' }}>
              needs evidence
            </span>
          ) : null}
        </div>

        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <RationaleHint
            source={field.source}
            rationale={field.rationale}
            modelUsed={field.modelUsed}
            generatedAt={field.generatedAt}
            editedAt={field.editedAt}
            emptyHint="No AI reasoning recorded. Refresh with AI to capture it."
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              toggle()
            }}
            aria-expanded={open}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] transition-colors hover:bg-[var(--color-warm-200)]"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <ChevronDown
              className="h-3 w-3 transition-transform"
              style={{ transform: open ? 'rotate(-180deg)' : 'rotate(0deg)' }}
            />
            derivation
          </button>
        </div>
      </div>

      {open ? <DerivationPanel derivation={derivation} reconcile={reconcile} unit={unit} /> : null}
    </div>
  )
}

function CountChip({
  icon,
  count,
  label,
  active,
}: {
  icon: React.ReactNode
  count: number
  label: string
  active: boolean
}) {
  return (
    <button
      type="button"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1 text-[11px] transition-colors"
      style={{
        color: active ? 'var(--color-business-600)' : 'var(--color-text-muted)',
      }}
    >
      <span className="flex items-center gap-1">
        {icon}
        <span className="font-medium tabular-nums">{count}</span>
      </span>
      <span>{label}</span>
    </button>
  )
}
